import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReactConfetti from 'react-confetti';
import emailService from '../services/emailService';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  AlertTitle,
  Avatar,
  Divider,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  IconButton,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  Fade,
  Stack,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';

// Import Timeline components from @mui/lab instead of @mui/material
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot
} from '@mui/lab';

// Import icons
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EmailIcon from '@mui/icons-material/Email';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LaunchIcon from '@mui/icons-material/Launch';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ArticleIcon from '@mui/icons-material/Article';
import ForumIcon from '@mui/icons-material/Forum';
import FavoriteIcon from '@mui/icons-material/Favorite';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import SearchIcon from '@mui/icons-material/Search';
import UpdateIcon from '@mui/icons-material/Update';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PendingIcon from '@mui/icons-material/Pending';
import BusinessIcon from '@mui/icons-material/Business';
import DomainIcon from '@mui/icons-material/Domain';
import InfoIcon from '@mui/icons-material/Info';
import CancelIcon from '@mui/icons-material/Cancel';

// Import services
import tenantRequestService from '../services/tenantRequestService';

const PendingApprovalPage = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [activeStep, setActiveStep] = useState(1);
  const [expanded, setExpanded] = useState(false);
  const [likeCount, setLikeCount] = useState(42);
  const [liked, setLiked] = useState(false);
  const [featuredResource, setFeaturedResource] = useState(0);
  
  // Add state for confetti animation
  const [showConfetti, setShowConfetti] = useState(false);
  const [previousStatus, setPreviousStatus] = useState(null);
  
  // Add state for status checking form
  const [statusCheckForm, setStatusCheckForm] = useState({
    subdomain: '',
    email: '',
  });
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [statusResult, setStatusResult] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [lastCheckedTime, setLastCheckedTime] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshCountdown, setRefreshCountdown] = useState(0);

  const [requestData, setRequestData] = useState({
    organizationName: '',
    subdomain: '',
    adminEmail: '',
    adminName: '',
    phoneNumber: '',
    location: '',
    industry: '',
    companySize: '',
    requestDate: new Date().toLocaleDateString()
  });

  // Get window dimensions for confetti
  const [windowDimensions, setWindowDimensions] = useState({ 
    width: typeof window !== 'undefined' ? window.innerWidth : 1000, 
    height: typeof window !== 'undefined' ? window.innerHeight : 800 
  });

  // Update window dimensions when resized
  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get current time to estimate review completion
  const now = new Date();
  const estimatedCompletionDate = new Date(now.setHours(now.getHours() + 24));

  // Array of featured resources to cycle through
  const resources = [
    {
      title: "Getting Started with Your Portal",
      description: "Learn how to set up your organization portal after approval with our step-by-step guide.",
      image: "/images/getting-started.jpg",
      type: "Guide",
      icon: <ArticleIcon />
    },
    {
      title: "Best Practices for Onboarding Members",
      description: "Discover strategies to effectively bring members into your organization.",
      image: "/images/onboarding.jpg",
      type: "Video",
      icon: <VideoLibraryIcon />
    },
    {
      title: "Configuring Your Organization Settings",
      description: "Learn how to customize and optimize your organization portal for your specific needs.",
      image: "/images/settings-config.jpg",
      type: "Tutorial",
      icon: <ArticleIcon />
    }
  ];

  // Change featured resource every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setFeaturedResource((prev) => (prev + 1) % resources.length);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Function to handle status check form input changes
  const handleStatusInputChange = (e) => {
    setStatusCheckForm({
      ...statusCheckForm,
      [e.target.name]: e.target.value
    });
  };

  // Function to check request status
  const checkRequestStatus = async (e) => {
    if (e) e.preventDefault();
    
    try {
      setIsCheckingStatus(true);
      setStatusError('');
      
      const result = await tenantRequestService.checkRequestStatus(
        statusCheckForm.subdomain || requestData.subdomain, 
        statusCheckForm.email || requestData.adminEmail
      );
      
      console.log('Tenant request status result:', result);
      setStatusResult(result.data);
      setLastCheckedTime(new Date());
      
      // Store the previous status for comparison
      const previousStatusValue = previousStatus;
      
      // If we have result data, update our local request data
      if (result.data) {
        setRequestData({
          organizationName: result.data.name || requestData.organizationName,
          subdomain: result.data.subdomain || requestData.subdomain,
          adminEmail: statusCheckForm.email || requestData.adminEmail,
          adminName: requestData.adminName,
          phoneNumber: requestData.phoneNumber,
          location: requestData.location,
          industry: result.data.industry || requestData.industry,
          companySize: result.data.companySize || requestData.companySize,
          requestDate: result.data.createdAt ? new Date(result.data.createdAt).toLocaleDateString() : requestData.requestDate,
          status: result.data.status || 'requested',
          // Store the approved tenant information if available
          tenantInfo: result.data.tenant || null
        });
        
        // Update stepper status based on request status
        switch(result.data.status) {
          case 'requested':
            setActiveStep(1); // Under Review
            break;
          case 'pending_info':
            setActiveStep(1); // Under Review but needs more info
            break;
          case 'approved':
            console.log('Setting active step to 2 for approved status');
            setActiveStep(2); // Approved 
            break;
          case 'rejected':
            setActiveStep(1); // Still at review step but rejected
            break;
          default:
            setActiveStep(1);
        }

        // Force UI refresh for approval status
        if (result.data.status === 'approved') {
          // Small timeout to ensure state updates properly
          setTimeout(() => {
            console.log('Confirmed approval status with activeStep:', 2);
          }, 0);
        }

        // Trigger confetti animation if status changes to approved
        if (result.data.status === 'approved' && previousStatusValue !== 'approved') {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 5000); // Stop confetti after 5 seconds
        }
        
        // Send email notification if status has changed
        if (previousStatusValue && previousStatusValue !== result.data.status) {
          try {
            const userEmail = statusCheckForm.email || requestData.adminEmail;
            const orgName = result.data.name || requestData.organizationName;
            
            const getStatusLabel = (status) => {
              switch(status) {
                case 'approved': return 'Approved';
                case 'rejected': return 'Rejected';
                case 'pending_info': return 'Additional Information Required';
                case 'requested': return 'Under Review';
                default: return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
              }
            };
            
            // Create the email content based on the new status
            let emailContent = `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <div style="background: linear-gradient(90deg, #3a7bd5, #00d2ff); padding: 20px; text-align: center; color: white;">
                  <h1>Status Update</h1>
                </div>
                <div style="padding: 20px; border: 1px solid #e0e0e0; border-top: none;">
                  <p>Hello,</p>
                  <p>There has been an update to your organization registration request for <strong>${orgName}</strong>.</p>
                  <p>Your request status has changed from <strong>${getStatusLabel(previousStatusValue)}</strong> to <strong>${getStatusLabel(result.data.status)}</strong>.</p>
            `;
            
            // Add appropriate content based on the new status
            if (result.data.status === 'approved') {
              emailContent += `
                <p>Your organization portal has been approved and is now ready to use!</p>
                <p>You can log in to your portal using the credentials you provided during registration.</p>
                <div style="text-align: center; margin: 30px 0;">
                  <a href="https://${result.data.subdomain}.yourdomain.com/login" style="background: linear-gradient(90deg, #4CAF50, #8BC34A); color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">Access Your Portal</a>
                </div>
              `;
            } else if (result.data.status === 'rejected') {
              emailContent += `
                <p>Unfortunately, your organization registration request has been rejected.</p>
                <p><strong>Reason:</strong> ${result.data.rejectionReason || 'No reason provided'}</p>
                <p>If you have questions or would like to submit a new request with different information, please contact our support team.</p>
              `;
            } else if (result.data.status === 'pending_info') {
              emailContent += `
                <p>Our team needs additional information to process your request:</p>
                <p><strong>Information Needed:</strong> ${result.data.adminNotes || 'Please check your account for details.'}</p>
                <div style="text-align: center; margin: 30px 0;">
                  <a href="${window.location.origin}/pending-approval" style="background: linear-gradient(90deg, #3a7bd5, #00d2ff); color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">Respond to Request</a>
                </div>
              `;
            }
            
            // Close the email HTML
            emailContent += `
                <p>If you have any questions, please contact our support team.</p>
                <hr style="border: none; border-top: 1px solid #e0e0e0; margin: 20px 0;">
                <p style="color: #666; font-size: 12px;">© ${new Date().getFullYear()} Transform Portal</p>
              </div>
            </div>
            `;
            
            // Send the notification email
            await emailService.sendEmail({
              to: userEmail,
              subject: `Status Update: ${orgName} - ${getStatusLabel(result.data.status)}`,
              html: emailContent
            });
            
            console.log('Status update email sent successfully');
          } catch (emailError) {
            console.error('Failed to send status update email:', emailError);
            // Don't block the process if email sending fails
          }
        }
        
        setPreviousStatus(result.data.status);
      }
      
    } catch (error) {
      console.error("Error checking status:", error);
      
      // Provide more helpful error messages for common scenarios
      if (error.response?.status === 404) {
        setStatusError(
          <Box>
            <Typography variant="body1" gutterBottom>
              No matching tenant request found. Please verify your information.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Possible reasons:
            </Typography>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>The subdomain or email might be incorrect</li>
              <li>Your request may have been processed under a different email</li>
              <li>Your request may not have been submitted successfully</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Need help? Contact support at <Link href="mailto:support@membasuite.com" style={{ color: 'inherit', textDecoration: 'underline' }}>support@example.com</Link>
            </Typography>
          </Box>
        );
      } else {
        setStatusError(error.response?.data?.message || 'Failed to check status. Please try again.');
      }
    } finally {
      setIsCheckingStatus(false);
    }
  };
  
  // Effect to handle auto-refresh countdown
  useEffect(() => {
    let timer;
    
    if (autoRefresh && refreshCountdown > 0) {
      timer = setTimeout(() => {
        setRefreshCountdown(refreshCountdown - 1);
      }, 1000);
    } else if (autoRefresh && refreshCountdown === 0) {
      // Time to refresh
      checkRequestStatus();
      setRefreshCountdown(60); // Reset to 60 seconds
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [autoRefresh, refreshCountdown]);

  // Toggle auto-refresh status
  const toggleAutoRefresh = () => {
    if (!autoRefresh) {
      setRefreshCountdown(60);
    }
    setAutoRefresh(!autoRefresh);
  };

  // Load the actual request data when the component mounts
  useEffect(() => {
    // Try to get data from location state (passed during navigation)
    const stateData = location.state?.requestData;
    
    if (stateData) {
      setRequestData({
        organizationName: stateData.name || stateData.organizationName || '',
        subdomain: stateData.subdomain || '',
        adminEmail: stateData.requestedBy?.email || stateData.adminEmail || '',
        adminName: `${stateData.requestedBy?.firstName || ''} ${stateData.requestedBy?.lastName || ''}`.trim(),
        phoneNumber: stateData.phone?.full || '',
        location: stateData.location?.formatted || '',
        industry: stateData.industry || '',
        companySize: stateData.companySize || '',
        requestDate: stateData.createdAt ? new Date(stateData.createdAt).toLocaleDateString() : new Date().toLocaleDateString()
      });
      return;
    }

    // If no state data, try localStorage
    try {
      const storedRequest = localStorage.getItem('tenantRequest');
      if (storedRequest) {
        const parsedData = JSON.parse(storedRequest);
        setRequestData({
          organizationName: parsedData.name || parsedData.organizationName || '',
          subdomain: parsedData.subdomain || '',
          adminEmail: parsedData.requestedBy?.email || parsedData.adminEmail || parsedData.ownerEmail || '',
          adminName: parsedData.requestedBy ? 
            `${parsedData.requestedBy.firstName || ''} ${parsedData.requestedBy.lastName || ''}`.trim() : '',
          phoneNumber: parsedData.phone?.full || 
            (parsedData.phone ? `${parsedData.phone.countryCode || ''} ${parsedData.phone.areaCode || ''} ${parsedData.phone.number || ''}`.trim() : ''),
          location: parsedData.location?.formatted || 
            (parsedData.location ? `${parsedData.location.city || ''}, ${parsedData.location.state || ''}, ${parsedData.location.country || ''}`.trim() : ''),
          industry: parsedData.industry || '',
          companySize: parsedData.companySize || '',
          requestDate: parsedData.createdAt ? new Date(parsedData.createdAt).toLocaleDateString() : new Date().toLocaleDateString()
        });
      }
    } catch (error) {
      console.error("Error loading request data:", error);
    }
    
    // When component loads, try to prefill the form with stored data
    if (requestData.subdomain) {
      setStatusCheckForm(prev => ({
        ...prev,
        subdomain: requestData.subdomain
      }));
    }
    
    if (requestData.adminEmail) {
      setStatusCheckForm(prev => ({
        ...prev,
        email: requestData.adminEmail
      }));
    }
    
    // Check status automatically if we have both subdomain and email
    if (requestData.subdomain && requestData.adminEmail) {
      checkRequestStatus();
    }
  }, [location]);

  const handleLike = () => {
    if (!liked) {
      setLikeCount(likeCount + 1);
    } else {
      setLikeCount(likeCount - 1);
    }
    setLiked(!liked);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f8fafc',
        backgroundImage: 'linear-gradient(180deg, rgba(236, 246, 255, 0.8) 0%, rgba(249, 250, 255, 1) 100%)',
        py: { xs: 4, md: 6 },
      }}
    >
      {showConfetti && (
        <ReactConfetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          recycle={false}
        />
      )}
      <Container maxWidth="lg">
        <Fade in={true} timeout={800}>
          <Box>
            {/* Header Section */}
            <Box textAlign="center" mb={4}>
              <Typography
                variant="h3"
                component="h1"
                fontWeight="800"
                sx={{
                  mb: 2,
                  background: 'linear-gradient(90deg, #3366FF 0%, #00CCFF 100%)',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {statusResult?.status === 'approved' ? 'Your Organization is Ready!' : 'Your Journey Has Begun'}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
                {statusResult?.status === 'approved' 
                  ? 'Your registration has been approved and your portal is ready to use'
                  : statusResult?.status === 'rejected'
                  ? 'Unfortunately, your registration request has been rejected'
                  : "We're reviewing your registration and preparing your enterprise portal"}
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {/* Main Status Card */}
              <Grid item xs={12} md={7} lg={8}>
                <Paper
                  elevation={4}
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: 3,
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Status Indicator */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: statusResult?.status === 'approved' ? 'success.main' : 
                                statusResult?.status === 'rejected' ? 'error.main' : 'warning.main',
                        mr: 1.5,
                        animation: statusResult?.status !== 'approved' && statusResult?.status !== 'rejected' ? 
                                  'pulse 1.5s infinite' : 'none',
                        '@keyframes pulse': {
                          '0%': { boxShadow: '0 0 0 0 rgba(255, 167, 38, 0.7)' },
                          '70%': { boxShadow: '0 0 0 10px rgba(255, 167, 38, 0)' },
                          '100%': { boxShadow: '0 0 0 0 rgba(255, 167, 38, 0)' },
                        }
                      }}
                    />
                    <Typography 
                      variant="subtitle1" 
                      fontWeight="600" 
                      color={
                        statusResult?.status === 'approved' ? 'success.main' : 
                        statusResult?.status === 'rejected' ? 'error.main' : 
                        statusResult?.status === 'pending_info' ? 'info.main' : 
                        'warning.main'
                      }
                    >
                      {statusResult?.status === 'approved' ? 'Approved' :
                       statusResult?.status === 'rejected' ? 'Rejected' :
                       statusResult?.status === 'pending_info' ? 'Additional Info Required' :
                       'Review in Progress'}
                    </Typography>
                    <Box flexGrow={1} />
                    <Chip
                      icon={<HourglassEmptyIcon />}
                      label={statusResult?.status === 'approved' ? 'Completed' : 
                             `Est. ${estimatedCompletionDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}`}
                      color={statusResult?.status === 'approved' ? 'success' : 'primary'}
                      variant="outlined"
                      size="small"
                    />
                  </Box>

                  {/* Status Check Form */}
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 3, 
                      mb: 3, 
                      bgcolor: 'primary.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'primary.100'
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                      Check Your Application Status
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Enter your subdomain and email address to see the latest status of your registration.
                    </Typography>
                    
                    <form onSubmit={checkRequestStatus}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            name="subdomain"
                            label="Subdomain"
                            value={statusCheckForm.subdomain}
                            onChange={handleStatusInputChange}
                            placeholder="your-subdomain"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <DomainIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                            required
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            name="email"
                            label="Admin Email"
                            value={statusCheckForm.email}
                            onChange={handleStatusInputChange}
                            placeholder="admin@example.com"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EmailIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                            required
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<SearchIcon />}
                            type="submit"
                            disabled={isCheckingStatus}
                            sx={{ height: '100%' }}
                          >
                            {isCheckingStatus ? 'Checking...' : 'Check'}
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                    
                    {statusError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {statusError}
                      </Alert>
                    )}
                    
                    {lastCheckedTime && !statusError && (
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mt: 2
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Last checked: {lastCheckedTime.toLocaleTimeString()}
                        </Typography>
                        
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={autoRefresh}
                              onChange={toggleAutoRefresh}
                              color="primary"
                            />
                          }
                          label={
                            <Typography variant="caption">
                              Auto-refresh {autoRefresh ? `(${refreshCountdown}s)` : ''}
                            </Typography>
                          }
                        />
                      </Box>
                    )}
                  </Paper>

                  {/* Status-specific messages */}
                  {statusResult?.status === 'approved' && (
                    <Alert 
                      severity="success" 
                      icon={<VerifiedUserIcon />}
                      sx={{ mb: 3 }}
                    >
                      <AlertTitle>Registration Approved!</AlertTitle>
                      Your organization portal has been approved and is ready to use.
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Tenant Information:</strong> {requestData.tenantInfo?.name || 'Not available'}
                      </Typography>
                      <Button 
                        variant="outlined" 
                        color="success" 
                        size="small" 
                        sx={{ mt: 1 }}
                        href={`https://${statusResult.subdomain}.yourdomain.com/login`}
                        target="_blank"
                      >
                        Log in to your portal
                      </Button>
                    </Alert>
                  )}
                  
                  {statusResult?.status === 'rejected' && (
                    <Alert 
                      severity="error" 
                      icon={<ErrorOutlineIcon />}
                      sx={{ mb: 3 }}
                    >
                      <AlertTitle>Registration Rejected</AlertTitle>
                      Unfortunately, your registration request has been rejected.
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Reason:</strong> {statusResult.rejectionReason || 'No reason provided'}
                      </Typography>
                    </Alert>
                  )}
                  
                  {statusResult?.status === 'pending_info' && (
                    <Alert 
                      severity="info" 
                      icon={<InfoIcon />}
                      sx={{ mb: 3 }}
                    >
                      <AlertTitle>Additional Information Required</AlertTitle>
                      Our team needs more information to process your application:
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {statusResult.adminNotes}
                      </Typography>
                      <Button 
                        variant="outlined" 
                        color="info" 
                        size="small" 
                        sx={{ mt: 1 }}
                        onClick={() => {/* Function to show response form */}}
                      >
                        Provide Additional Information
                      </Button>
                    </Alert>
                  )}

                  <Grid container spacing={4}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
                        Approval Status
                      </Typography>

                      <Typography variant="body1" paragraph>
                        {statusResult?.status === 'approved' ? 
                          'Your organization has been successfully approved and is ready for use. You can now log in using your administrator credentials.' :
                          'Your administrator account and organization portal have been successfully registered and are now awaiting approval.'}
                      </Typography>

                      <Alert
                        severity={statusResult?.status === 'approved' ? "success" : "info"}
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          alignItems: 'flex-start'
                        }}
                      >
                        <Typography variant="body2">
                          {statusResult?.status === 'approved' ? (
                            <>
                              <strong>Next Steps:</strong> Log in to your portal to customize your organization settings and start inviting members.
                            </>
                          ) : (
                            <>
                              <strong>Next Steps:</strong> You'll receive an email notification once approved, typically within 24 hours. No action is required from you at this time.
                            </>
                          )}
                        </Typography>
                      </Alert>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Card
                        sx={{
                          bgcolor: 'rgba(25, 118, 210, 0.04)',
                          border: '1px solid rgba(25, 118, 210, 0.1)',
                          boxShadow: 'none',
                          mb: 3,
                          borderRadius: 2
                        }}
                      >
                        <CardContent>
                          <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="medium">
                            Application Summary
                          </Typography>

                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ width: 140, flexShrink: 0 }}>
                              Organization:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {requestData.organizationName || 'Not specified'}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ width: 140, flexShrink: 0 }}>
                              Subdomain:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {requestData.subdomain || 'Not specified'}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ width: 140, flexShrink: 0 }}>
                              Admin Contact:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {requestData.adminName ? (
                                <>
                                  {requestData.adminName}
                                  <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
                                    {requestData.adminEmail}
                                  </Typography>
                                </>
                              ) : requestData.adminEmail || 'Not specified'}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ width: 140, flexShrink: 0 }}>
                              Phone Number:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {requestData.phoneNumber || 'Not specified'}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ width: 140, flexShrink: 0 }}>
                              Location:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {requestData.location || 'Not specified'}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ width: 140, flexShrink: 0 }}>
                              Industry:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {requestData.industry || 'Not specified'}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ width: 140, flexShrink: 0 }}>
                              Company Size:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {requestData.companySize || 'Not specified'}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ width: 140, flexShrink: 0 }}>
                              Requested on:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {requestData.requestDate}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        {statusResult?.status === 'approved' ? (
                          <>
                            <Button
                              variant="outlined"
                              color="inherit"
                              startIcon={<ArrowBackIcon />}
                              component={Link}
                              to="/"
                              fullWidth
                            >
                              Return Home
                            </Button>

                            <Button
                              variant="contained"
                              color="success"
                              disableElevation
                              startIcon={<CheckCircleIcon />}
                              href={`https://${requestData.subdomain}.membasuite.com/login`}
                              target="_blank"
                              fullWidth
                            >
                              Log In Now
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="outlined"
                              color="inherit"
                              startIcon={<ArrowBackIcon />}
                              component={Link}
                              to="/"
                              fullWidth
                            >
                              Return Home
                            </Button>

                            <Button
                              variant="contained"
                              disableElevation
                              startIcon={<RefreshIcon />}
                              onClick={checkRequestStatus}
                              fullWidth
                            >
                              Check Status
                            </Button>
                          </>
                        )}
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }} />

                  {/* Progress Indicator */}
                  <Box>
                    <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                      Application Progress
                    </Typography>
                    <Stepper activeStep={activeStep} alternativeLabel={!isMobile}>
                      <Step key="submitted">
                        <StepLabel>Submitted</StepLabel>
                      </Step>
                      <Step key="reviewing">
                        <StepLabel
                          error={statusResult?.status === 'rejected'}
                          optional={statusResult?.status === 'rejected' ? 
                            <Typography variant="caption" color="error">Rejected</Typography> :
                            statusResult?.status === 'pending_info' ?
                            <Typography variant="caption" color="info.main">Needs Info</Typography> : null
                          }
                        >
                          Under Review
                        </StepLabel>
                      </Step>
                      <Step key="approved">
                        <StepLabel>Approved</StepLabel>
                      </Step>
                    </Stepper>
                  </Box>

                  {/* Status History */}
                  {statusResult?.statusHistory && statusResult.statusHistory.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        Status History
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 0 }}>
                        <Timeline position={isMobile ? "right" : "alternate"} sx={{ m: 0, p: 2 }}>
                          {statusResult.statusHistory.map((history, index) => (
                            <TimelineItem key={index}>
                              <TimelineSeparator>
                                <TimelineDot 
                                  color={
                                    history.status === 'approved' ? 'success' :
                                    history.status === 'rejected' ? 'error' :
                                    history.status === 'pending_info' ? 'info' :
                                    'primary'
                                  }
                                >
                                  {history.status === 'approved' ? <CheckCircleIcon /> :
                                   history.status === 'rejected' ? <CancelIcon /> :
                                   history.status === 'pending_info' ? <InfoIcon /> :
                                   history.status === 'requested' ? <PendingIcon /> :
                                   <BusinessIcon />
                                  }
                                </TimelineDot>
                                {index < statusResult.statusHistory.length - 1 && <TimelineConnector />}
                              </TimelineSeparator>
                              <TimelineContent>
                                <Typography variant="body2" fontWeight="medium">
                                  Status changed to {history.status.replace('_', ' ')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {new Date(history.changedAt).toLocaleString()}
                                </Typography>
                                {history.reason && (
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {history.reason}
                                  </Typography>
                                )}
                              </TimelineContent>
                            </TimelineItem>
                          ))}
                        </Timeline>
                      </Paper>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Right Sidebar */}
              <Grid item xs={12} md={5} lg={4}>
                <Stack spacing={3}>
                  {/* Contact Support Card */}
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.07)',
                      display: 'flex',
                      flexDirection: 'column',
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <SupportAgentIcon color="primary" sx={{ mr: 1.5 }} />
                      <Typography variant="h6" fontWeight="bold">
                        Need Assistance?
                      </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" paragraph>
                      Our support team is available to help you during the review process. If you have any questions or concerns, please don't hesitate to reach out.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<EmailIcon />}
                        href="mailto:support@emembasuite.com"
                        fullWidth
                        size="small"
                      >
                        Email Support
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ForumIcon />}
                        href="#live-chat"
                        color="secondary"
                        fullWidth
                        size="small"
                      >
                        Live Chat
                      </Button>
                    </Box>
                  </Paper>

                  {/* Featured Resource Card */}
                  <Fade in={true} timeout={500}>
                    <Paper
                      elevation={3}
                      sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.07)',
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="140"
                        image={resources[featuredResource].image || "https://via.placeholder.com/600x140"}
                        alt="Resource thumbnail"
                      />
                      <CardContent sx={{ pb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Chip
                            icon={resources[featuredResource].icon}
                            label={resources[featuredResource].type}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Box flexGrow={1} />
                          <Tooltip title={liked ? "Unmark as helpful" : "Mark as helpful"}>
                            <IconButton
                              size="small"
                              onClick={handleLike}
                              color={liked ? "primary" : "default"}
                            >
                              <FavoriteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary">
                            {likeCount}
                          </Typography>
                        </Box>

                        <Typography variant="h6" component="div" gutterBottom fontWeight="bold">
                          {resources[featuredResource].title}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" paragraph>
                          {resources[featuredResource].description}
                        </Typography>

                        <Button
                          variant="text"
                          endIcon={<LaunchIcon />}
                          size="small"
                          sx={{ mt: 1 }}
                        >
                          View Resource
                        </Button>
                      </CardContent>
                    </Paper>
                  </Fade>

                  {/* Popular Questions */}
                  <Paper
                    elevation={3}
                    sx={{
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.07)',
                    }}
                  >
                    <Box sx={{
                      p: 2,
                      bgcolor: theme.palette.mode === 'dark' ? 'primary.dark' : 'primary.light',
                      color: theme.palette.mode === 'dark' ? 'white' : 'primary.main',
                    }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Popular Questions
                      </Typography>
                    </Box>

                    <Accordion
                      expanded={expanded === 'panel1'}
                      onChange={handleAccordionChange('panel1')}
                      elevation={0}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2" fontWeight="medium">
                          How long does approval take?
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" color="text.secondary">
                          Approval typically takes 24-48 hours during business days. You'll receive an email notification once your account is approved.
                        </Typography>
                      </AccordionDetails>
                    </Accordion>

                    <Accordion
                      expanded={expanded === 'panel2'}
                      onChange={handleAccordionChange('panel2')}
                      elevation={0}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2" fontWeight="medium">
                          Can I change my subdomain later?
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" color="text.secondary">
                          While subdomain changes are possible, they require administrator assistance and can cause disruption. We recommend choosing carefully from the start.
                        </Typography>
                      </AccordionDetails>
                    </Accordion>

                    <Accordion
                      expanded={expanded === 'panel3'}
                      onChange={handleAccordionChange('panel3')}
                      elevation={0}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2" fontWeight="medium">
                          What happens after approval?
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" color="text.secondary">
                          Once approved, you can log in using your credentials, customize your organization settings, and begin inviting members to your portal.
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  </Paper>
                </Stack>
              </Grid>

              {/* Lower Section - Feature Preview */}
              <Grid item xs={12}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.07)',
                    background: 'linear-gradient(to right, #1A237E, #0D47A1)',
                    color: 'white',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <WhatshotIcon sx={{ mr: 1.5 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Membasuite Portal
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                        <CheckCircleIcon sx={{ mr: 1.5, color: '#4FC3F7' }} />
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Member Management
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Easily manage your organization's members, roles, and permissions.
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                        <CheckCircleIcon sx={{ mr: 1.5, color: '#4FC3F7' }} />
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Custom Branding
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Apply your organization's colors, logo, and styling to your portal.
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                        <CheckCircleIcon sx={{ mr: 1.5, color: '#4FC3F7' }} />
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Analytics Dashboard
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Gain insights into membership activity and engagement metrics.
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      sx={{
                        bgcolor: 'white',
                        color: '#1A237E',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.9)'
                        }
                      }}
                    >
                      Watch Platform Demo
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Footer Info */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Have questions? Contact support at <Link href="mailto:support@membasuite.com" style={{ color: theme.palette.primary.main }}>support@membasuite.com</Link>
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default PendingApprovalPage;
